import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createSlots as _createSlots } from "vue"

import { alertController } from "@ionic/vue";
import { computed, ref } from "vue";
import router from "@/router";
import { t } from "@/plugins/vue-i18n";

import ButtonSliding from "@/components/button/ButtonSliding.vue";
import OrderDetailsEntry from "@/components/various/OrderDetailsEntry.vue";
import SliderOptionDeleteBoxed from "@/components/sliderOptions/SliderOptionDeleteBoxed.vue";
import SliderOptionAmount from "@/components/sliderOptions/SliderOptionAmount.vue";

import { useListStore } from "@/stores/lists";
import { useUserStore } from "@/stores/user";
import { useNotificationStore } from "@/stores/notification";

import { useLoader } from "@/composables/useLoader";

import { interactionFeedbackToast } from "@/functions/baseToast";
import { popAlertConfirm } from "@/functions/alert";

import { PERMISSION_KEY } from "@/types/user";
import { CartDetailsSliding } from "@/types/props";


export default /*@__PURE__*/_defineComponent({
  __name: 'CartDetailsSliding',
  props: {
    entry: {},
    edit: { type: Boolean },
    noTop: { type: Boolean }
  },
  emits: ["amount", "enable"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

const listStore = useListStore()
const userStore = useUserStore()
const notificationStore = useNotificationStore()
const { showLoading, hideLoading } = useLoader()

const orderDetailSlider = ref()

const promotionParams = computed(() => {
    const promotion = props.entry.item.promotion;
    if (!promotion && !props.entry.promotionId /* fallback == not harmonized */) return {};
    const product = notificationStore.findPromotionProduct(promotion?.id || props.entry.promotionId, promotion?.productId || props.entry.productId) /* fallback == not harmonized */
    return { stepAmount: product?.stepAmount, limitOrderSize: product?.limitOrderSize }
})

const amount = computed<number>({
    get: () => {
        return props.entry.quantity
    },
    set: (value) => {
        handleAmount(value)
    }
})

const handleAmount = (amount?: number): void => {
    if (!amount) deleteCartItemCheck()
    emit('amount', amount);
}

const handleDeleteClick = (event: MouseEvent | TouchEvent): void => {
    event.stopPropagation()
    deleteCartItemCheck(amount.value)
}

const deleteCartItemCheck = async (amountBefore?: number): Promise<void> => {
    if (!amountBefore) emit('amount', 0)
    if (await alertController.getTop()) return
    await popAlertConfirm({
        message: t('alert.delete.cartProduct', { target: props.entry.item.name }),
        handler: {
            ok: deleteCartItem,
            cancel: () => emit('amount', amountBefore ?? 1)
        }
    })
}

const deleteCartItem = async (): Promise<void> => {
    await showLoading()

    const response = await listStore.deleteCartItem(router.currentRoute.value.params.locationId as string, props.entry.id)

    emit('enable')

    interactionFeedbackToast(
        response,
        {
            type: 'removeProduct',
            id: 'remove-product'
        },
        {
            cssClass: 'toast-offset-70'
        }
    )
    await hideLoading()

    if(response) await listStore.getShoppingCart(router.currentRoute.value.params.locationId as string)
}

const convertToPromotionCheck = async (): Promise<void> => {
    if (await alertController.getTop()) return

    const existInCart = listStore.promotionItemInCart(props.entry.supplierId as string, props.entry.supplierItemId)
    const cartQuantity = existInCart ? existInCart.quantity : 0;

    const quantity = props.entry.quantity;
    const { stepAmount, limitOrderSize } = promotionParams.value;

    // Check if the SUM of this order and the order from the promotion section is less than or equal to the LIMIT ORDER SIZE.
    if (stepAmount && limitOrderSize && (stepAmount + cartQuantity > limitOrderSize)) {
        interactionFeedbackToast(
            false,
            { type: 'promotionLimitReached', id: 'promotion-limit-reached'},
            { cssClass: 'toast-offset-70' }
        )
        return
    }
    
    if (stepAmount && quantity < stepAmount) { 
        await popAlertConfirm({
            message: t('alert.upgradeToPromotion', { target: stepAmount }),
            handler: {
                ok: upgradeToPromotion,
                // cancel: () => {}
            }
        })
    } 

    if (stepAmount && quantity >= stepAmount) {

        const promotionItemQty = limitOrderSize && ((quantity + cartQuantity) > limitOrderSize)
            ? limitOrderSize
            : quantity + cartQuantity - (quantity % stepAmount)

        const regularItemQty = quantity + cartQuantity - promotionItemQty;

        await popAlertConfirm({
            message: `${t('alert.convertToPromotion1')} ${regularItemQty !== 0 ? `${t('alert.convertToPromotion2', { target1: promotionItemQty, target2: regularItemQty })}` : ''}`,
            handler: {
                ok: () => convertToPromotion(promotionItemQty - cartQuantity, regularItemQty),
                // cancel: () => {}
            }
        })
    }
}

const upgradeToPromotion = async (): Promise<void> => {
    await showLoading()
    const response = await listStore.deleteCartItem(router.currentRoute.value.params.locationId as string, props.entry.id)
    if (response) await listStore.addInformationItemToCart({
        locationId: router.currentRoute.value.params.locationId as string,
        informationId: props.entry.item.promotion?.id as string,
        productId: props.entry.item.promotion?.productId as string,
        quantity: promotionParams.value.stepAmount as number,
        supplierId: props.entry.supplierId
    })
    
    await listStore.getShoppingCart(router.currentRoute.value.params.locationId as string)
    await hideLoading()
}

const convertToPromotion = async (promotionItemQty: number, regularItemQty: number): Promise<void> => {
    await showLoading()
    const response = regularItemQty === 0
        ? await listStore.deleteCartItem(router.currentRoute.value.params.locationId as string, props.entry.id)
        : await listStore.patchCartItem(router.currentRoute.value.params.locationId as string, props.entry.id, regularItemQty)
    if (response && promotionItemQty > 0) await listStore.addInformationItemToCart({
        locationId: router.currentRoute.value.params.locationId as string,
        informationId: props.entry.item.promotion?.id as string,
        productId: props.entry.item.promotion?.productId as string,
        quantity: promotionItemQty,
        supplierId: props.entry.item.supplierId
    })
    
    await listStore.getShoppingCart(router.currentRoute.value.params.locationId as string)
    await hideLoading()
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ButtonSliding, {
    ref_key: "orderDetailSlider",
    ref: orderDetailSlider
  }, _createSlots({
    default: _withCtx(() => [
      _createVNode(OrderDetailsEntry, {
        class: _normalizeClass(["pb-3 light-light-background purple-purple-color", {'pt-2': !__props.noTop }]),
        entry: __props.entry,
        edit: __props.edit,
        "promotion-amount-step": promotionParams.value.stepAmount,
        onEdit: _cache[0] || (_cache[0] = ($event: any) => (orderDetailSlider.value.$el.open('end'))),
        onConvertToPromotion: convertToPromotionCheck
      }, null, 8, ["class", "entry", "edit", "promotion-amount-step"])
    ]),
    _: 2
  }, [
    (__props.edit)
      ? {
          name: "options-end",
          fn: _withCtx(() => [
            (_unref(userStore).permission[_unref(PERMISSION_KEY).CART_DELETE])
              ? (_openBlock(), _createBlock(SliderOptionDeleteBoxed, {
                  key: 0,
                  onClick: handleDeleteClick
                }))
              : _createCommentVNode("", true),
            (_unref(userStore).permission[_unref(PERMISSION_KEY).CART_UPDATE])
              ? (_openBlock(), _createBlock(SliderOptionAmount, {
                  key: 1,
                  modelValue: amount.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((amount).value = $event)),
                  "step-size": __props.entry.isPromotion ? promotionParams.value.stepAmount : __props.entry.item.amountStep,
                  "min-value": __props.entry.isPromotion ? promotionParams.value.stepAmount : __props.entry.item.amountStep,
                  "limit-order-size": __props.entry.isPromotion ? promotionParams.value.limitOrderSize : undefined,
                  "direct-input-disabled": __props.entry.isPromotion && Number(promotionParams.value.stepAmount) > 1
                }, null, 8, ["modelValue", "step-size", "min-value", "limit-order-size", "direct-input-disabled"]))
              : _createCommentVNode("", true)
          ]),
          key: "0"
        }
      : undefined
  ]), 1536))
}
}

})